import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Modal } from '@/components/custom/modal/modal';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FormCombobox } from '@/components/custom/form';
import { PlayfulCardButton } from '@/components/custom/playful-card';

const schema = Yup.object({
  title: Yup.string().required('Campo obrigatório'),
  url: Yup.string().required('Campo obrigatório'),
  'course-ids': Yup.array().of(Yup.string().nullable()).min(1, 'Campo obrigatório'),
  date: Yup.string().required('Campo obrigatório'),
  startTime: Yup.string().required('Campo obrigatório'),
  endTime: Yup.string().required('Campo obrigatório')
});

function initialValuesFor(liveClass) {
  if (!liveClass) {
    return { title: '', url: '', 'course-ids': [], date: '', startTime: '', endTime: '', about: '' };
  }

  return {
    title: liveClass.title || '',
    url: liveClass.url || '',
    'course-ids': liveClass.courses?.map(course => course.id) || [],
    date: moment(liveClass['initial-date']).format('YYYY-MM-DD'),
    startTime: moment(liveClass['initial-date']).format('HH:mm'),
    endTime: moment(liveClass['end-date']).format('HH:mm'),
    about: liveClass.about || ''
  };
}

export function LiveClassFormModal({ show, mode, liveClass, isSubmitting, onClose, onSubmit }) {
  const client = useClient();
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const getCourses = async () => {
      setLoadingCourses(true);
      try {
        const { data } = await client.fetch('courses/simple');
        setCourses(data || []);
      } catch {
        toast.error('Erro ao carregar cursos');
      }
      setLoadingCourses(false);
    };

    getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesFor(liveClass),
    validationSchema: schema,
    onSubmit: values => {
      onSubmit({
        title: values.title,
        about: values.about,
        url: values.url,
        'course-ids': values['course-ids'],
        'initial-date': `${values.date} ${values.startTime}`,
        'end-date': `${values.date} ${values.endTime}`
      });
    }
  });

  // A modal fica montada entre aberturas, e no modo criação os valores iniciais
  // são sempre os mesmos — o `enableReinitialize` não percebe mudança e o form
  // guardaria o que foi digitado antes. Por isso reseta a cada abertura.
  useEffect(() => {
    if (show) form.resetForm({ values: initialValuesFor(liveClass) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, liveClass]);

  const fieldError = field => form.touched[field] && form.errors[field];

  const courseOptions = React.useMemo(
    () =>
      courses.map(course => ({
        value: course.id,
        label: course.title
      })),
    [courses]
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      contentClassName="modal__content-sm"
    >
      <form
        onSubmit={form.handleSubmit}
        className="flex flex-col gap-4 p-2"
      >
        <div className="flex flex-col gap-1">
          <DialogTitle className="text-xl font-extrabold">{mode === 'edit' ? 'Editar aula ao vivo' : 'Nova aula ao vivo'}</DialogTitle>
          <DialogDescription className="mb-0 text-sm text-neutral-500">Preencha os campos abaixo para {mode === 'edit' ? 'atualizar' : 'agendar'} a aula.</DialogDescription>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="live-class-title">Título</Label>
          <Input
            id="live-class-title"
            name="title"
            value={form.values.title}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            aria-invalid={Boolean(fieldError('title'))}
          />
          {fieldError('title') && (
            <p
              className="mb-0 text-xs text-danger"
              role="alert"
            >
              {form.errors.title}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="live-class-url">URL</Label>
          <Input
            id="live-class-url"
            name="url"
            type="url"
            value={form.values.url}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            aria-invalid={Boolean(fieldError('url'))}
          />
          {fieldError('url') && (
            <p
              className="mb-0 text-xs text-danger"
              role="alert"
            >
              {form.errors.url}
            </p>
          )}
        </div>

        <FormCombobox
          label="Curso"
          options={courseOptions}
          value={form.values['course-ids']}
          placeholder={loadingCourses ? 'Carregando...' : 'Selecione o curso'}
          loading={loadingCourses}
          multiple
          onValueChange={value => form.setFieldValue('course-ids', value ?? [])}
          hasError={Boolean(fieldError('course-ids'))}
          errorText="Campo obrigatório"
        />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="live-class-date">Data</Label>
          <Input
            id="live-class-date"
            name="date"
            type="date"
            className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            value={form.values.date}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            aria-invalid={Boolean(fieldError('date'))}
          />
          {fieldError('date') && (
            <p
              className="mb-0 text-xs text-danger"
              role="alert"
            >
              {form.errors.date}
            </p>
          )}
        </div>

        <div className="flex gap-4">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="live-class-start-time">Início</Label>
            <Input
              id="live-class-start-time"
              name="startTime"
              type="time"
              className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              value={form.values.startTime}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              aria-invalid={Boolean(fieldError('startTime'))}
            />
            {fieldError('startTime') && (
              <p
                className="mb-0 text-xs text-danger"
                role="alert"
              >
                {form.errors.startTime}
              </p>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="live-class-end-time">Término</Label>
            <Input
              id="live-class-end-time"
              name="endTime"
              type="time"
              className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              value={form.values.endTime}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              aria-invalid={Boolean(fieldError('endTime'))}
            />
            {fieldError('endTime') && (
              <p
                className="mb-0 text-xs text-danger"
                role="alert"
              >
                {form.errors.endTime}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="live-class-about">Descrição</Label>
          <Textarea
            id="live-class-about"
            name="about"
            value={form.values.about}
            onChange={form.handleChange}
          />
        </div>

        <PlayfulCardButton
          type="submit"
          disabled={isSubmitting}
        >
          Salvar
        </PlayfulCardButton>
      </form>
    </Modal>
  );
}
